<template>
  <bb-functional-accordion open class="bg-gray-10 flex-1" :data-testid="tableTestId">
    <template #header="{ expanded }">
      <div class="d-flex justify-content-between f-color-navy">
        <h4 class="f-gotham-medium">{{ translations.title }}</h4>
        <bb-icon
          :rotate="expanded ? 'down' : 'up'"
          name="ui-chevron-down"
          size="24"
        />
      </div>
    </template>
    <available-balance-warning-banner
      v-if="availableBalanceWarning.visible"
      class="m-t-15"
      v-bind="availableBalanceWarning"/>
    <bb-public-table
      class="m-t-25"
      :columns="labeledColumns"
      :rows="tableRows"
      :footer-columns="footerColumns"
      :footer-rows="footerRows"
      :rows-state.sync="rowsState"
      :subrow-columns="subrowColumns"
      :subrow-toggle-label="translations.table.toggleDetails"
    >
      <template #header="{ column }">
        <bb-checkbox
          v-if="column.key === 'rowSelection' && !isNotConfirmedOrConfirmedStatus"
          data-testid="pending-master-select"
          v-model="checkboxMaster"
          :input-attrs="{ 'aria-label': translations.table.selectAllRows }"
          name="check_all"
          :title="translations.table.selectAllRows"
          size="small"
          no-padding
          @input="onMasterCheckboxInput"
        />
        <template v-else>
          <span class="d-block f-nowrap">
            {{ castArray(column.label)[0] }}
          </span>
          <span
            v-if="castArray(column.label)[1]"
            class="d-block f-nowrap f-color-gray-70"
          >
            {{ column.label[1] }}
          </span>
        </template>
      </template>
      <template #cell="{ column, rowIndex, value, row }">
        <bb-checkbox
          v-if="column.key === 'rowSelection' && !isNotConfirmedOrConfirmedStatus"
          data-testid="pending-row-selection"
          v-model="checkboxStates[row.id]"
          :input-attrs="{ 'aria-label': translations.table.selectRow }"
          :name="`check_row_${rowIndex}`"
          :title="translations.table.selectRow"
          size="small"
          no-padding
          @click.native.stop
        />
        <template v-else-if="column.key === 'rowActions'">
          <div class="d-flex gap-10 justify-content-end">
            <bb-button
              v-if="showModifyButton(row.status[0])"
              data-testid="pending-edit-button"
              color="black"
              size="sm"
              display="square"
              icon="ui-pencil"
              :aria-label="translations.button.edit"
              :title="translations.button.edit"
              inverted
              @click.stop="onModify(row.id)"
            />
            <bb-button
              v-if="showRecreateButton(row.status[0])"
              data-testid="pending-recreate-button"
              color="black"
              size="sm"
              display="square"
              icon="ui-autorenew"
              :aria-label="translations.button.recreate"
              :title="translations.button.recreate"
              inverted
              @click.stop="onRecreate(row.id)"
            />
            <bb-button
              v-if="showCancelButton(row.status[0])"
              data-testid="pending-cancel-button"
              color="black"
              size="sm"
              display="square"
              icon="ui-delete"
              :aria-label="translations.button.delete"
              :title="translations.button.delete"
              inverted
              @click.stop="onCancel(row.id)"
            />
          </div>
        </template>
        <template v-else-if="column.key === 'status'">
          <p
            class="f-gotham-bold f-small"
            :class="getPaymentStatusColor(value)"
          >
            {{ translations.status[value[0]] || value[0] }}
          </p>
          <p
            v-if="value[1]"
            class="f-gotham-bold f-color-gray-70 f-tiny"
          >
            {{ value[1] }}
          </p>
        </template>
        <template v-else>
          <p class="f-gotham-bold f-small f-color-gray">
            {{ castArray(value)[0] }}
          </p>
          <p
            v-if="castArray(value)[1]"
            class="f-gotham-bold f-color-gray-70 f-tiny"
          >
            {{ value[1] }}
          </p>
        </template>
      </template>
      <template #subrowCell="{ column, row, rowIndex }">
        <template v-if="column.key === 'details'">
          <p class="m-b-15 f-gotham-medium f-small f-color-navy">
            {{ translations.table.paymentDetails }}
          </p>
          <dl style="display: grid; grid-template-columns: auto 1fr; gap: 5px 40px;" data-testid="pending-details">
            <template v-for="item in subRowTemplate">
              <template v-for="(label, index) in castArray(columnLabels[item])">
                <dt
                  :key="`${item}-desc-${index + 1}`"
                  class="f-smallest f-gotham-bold f-color-gray"
                >
                  {{ label }}
                </dt>
                <dd
                  :key="`${item}-value-${index + 1}`"
                  class="f-smallest f-gotham-book f-color-gray"
                >
                  {{ castArray(row[item])[index] || '-' }}
                </dd>
              </template>
            </template>
          </dl>
          <bb-button
            data-testid="pending-close-button"
            color="black"
            size="sm"
            :label="translations.button.close"
            inverted
            style="position: absolute; top: 15px; right: 15px;"
            @click.stop="closeSubrow(rowIndex)"
          />
        </template>
      </template>
      <template #footerCell="{ value }">
        <p class="m-y-15 f-gotham-bold f-color-gray f-small">
          {{ value }}
        </p>
      </template>
    </bb-public-table>
    <div class="d-flex justify-content-end gap-15 m-t-15" v-if="!isNotConfirmedOrConfirmedStatus">
      <bb-button
        data-testid="pending-cancel-button"
        :label="translations.button.cancelSelected"
        :disabled="!hasTruthyCheckboxes"
        size="sm"
        color="gray"
        inverted
        @click="onCancelMany"
      />
      <bb-button
        data-testid="pending-confirm-button"
        :label="translations.button.confirm"
        :disabled="!hasTruthyCheckboxes || availableBalanceWarning.visible"
        size="sm"
        @click="selectForSigning"
      />
    </div>
  </bb-functional-accordion>
</template>

<script>
import { castArray } from 'lodash'
import pendingTableMixin from '@account/views/payments/pending/pending-table.mixin'
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { formatDate } from '@/plugins/dateFormatters'
import AvailableBalanceWarningBanner from '@account/views/payments/pending/AvailableBalanceWarningBanner.vue'

export default {
  name: 'pending-payment-table-desktop',
  components: { AvailableBalanceWarningBanner },
  mixins: [pendingTableMixin],
  data () {
    return {
      checkboxMaster: false,
      rowsState: {},
      subRowTemplate: [
        'fromIban',
        'dateTime',
        'beneficiary',
        'formattedAmount',
        'description',
        'referenceNumber',
        'status'
      ],
      footerColumns: [
        {
          offset: 4,
          type: 'th',
          key: 'label',
          class: 'border-left-radius-sm'
        },
        {
          key: 'amount',
          class: 'f-nowrap f-align-right'
        },
        {}
      ]
    }
  },
  computed: {
    subrowColumns () {
      return [
        ...(!this.isNotConfirmedOrConfirmedStatus ? [{ td: { 'aria-hidden': 'true' } }] : []),
        {
          key: 'details',
          class: 'relative p-y-15',
          td: {
            colspan: 6,
            'aria-label': this.translations.row.subPaymentDetails
          }
        }
      ]
    },
    labeledColumns () {
      const rowSelection = {
        key: 'rowSelection',
        class: 'p-r-5',
        th: {
          'aria-label': this.translations.row.selection
        }
      }
      const columns = [
        ...(!this.isNotConfirmedOrConfirmedStatus ? [rowSelection] : []),
        {
          key: 'beneficiary',
          class: this.isNotConfirmedOrConfirmedStatus ? 'p-l-25' : ''
        },
        { key: 'dateTime' },
        { key: 'descriptionAndReference' },
        {
          key: 'status',
          class: 'f-nowrap'
        },
        {
          key: 'formattedAmount',
          class: 'f-nowrap f-align-right'
        },
        {
          key: 'rowActions',
          class: 'p-l-5 p-r-25',
          th: {
            'aria-label': this.translations.row.actions
          }
        }
      ]

      return columns.map(column => ({
        ...column,
        label: this.columnLabels[column.key]
      }))
    },
    columnLabels () {
      const description = this.translations.column.description
      const referenceNumber = this.translations.column.referenceNumber

      return {
        fromIban: this.translations.column.fromIban,
        dateTime: this.translations.column.orderDate,
        status: this.translations.column.orderStatus,
        beneficiary: [
          this.translations.column.recipientName,
          this.translations.column.recipientIban
        ],
        descriptionAndReference: [
          description,
          referenceNumber
        ],
        referenceNumber,
        description,
        formattedAmount: this.translations.column.amount
      }
    },
    footerRows () {
      if (this.isNotConfirmedOrConfirmedStatus) return []
      const selectedRows = this.tableRows.filter((row) => this.checkboxStates[row.id])
      return [
        {
          label: this.translations.footer.totalAmount,
          amount: this.sumAmount
        },
        {
          label: this.translations.footer.totalPayments,
          amount: selectedRows.length
        }
      ]
    },
    tableRows () {
      return this.pendingPayments.map(pending => ({
        id: pending.id,
        fromIban: pending.fromIban,
        dateTime: formatDate(pending.orderDate),
        status: [pending.orderStatus, this.translations.statusDescription[pending.orderStatus] || ''],
        beneficiary: [
          pending.recipientName,
          pending.recipientIban
        ],
        description: pending.description,
        referenceNumber: pending.referenceNumber,
        descriptionAndReference: [pending.description || '-', pending.referenceNumber || '-'],
        amount: pending.amount,
        formattedAmount: formatMoneyWithCurrency(pending.amount, pending.currencyCode, this.locale)
      }))
    }
  },
  watch: {
    checkboxStates: {
      handler (checkboxStates) {
        const allEnabled = Object.values(checkboxStates).every(checkbox => checkbox === true)
        this.checkboxMaster = !!allEnabled
      },
      deep: true
    }
  },
  methods: {
    castArray,
    onMasterCheckboxInput () {
      this.checkboxStates = this.tableRows.reduce((acc, row) => {
        acc[row.id] = this.checkboxMaster
        return acc
      }, {})
    },
    closeSubrow (rowIndex) {
      this.$set(this.rowsState, rowIndex, false)
    }
  }
}
</script>
